import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  MessageCircle, 
  AlertTriangle,
  Package,
  Calendar,
  TrendingUp,
  Users,
  Home,
  Bell,
  Sparkles,
  ChevronRight,
  Mic,
  Send,
  Loader
} from 'lucide-react';

interface AIAlert {
  id: string;
  type: 'maintenance' | 'inventory' | 'guest' | 'staff';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
  aiSuggestion?: string;
  timestamp: string;
}

interface GuestMessage {
  id: string;
  guestName: string;
  room: string;
  message: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  intent: string;
  suggestedResponse: string;
  timestamp: string;
}

const PousadaMobileAI: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'ai' | 'alerts' | 'chat' | 'insights'>('ai');
  const [alerts, setAlerts] = useState<AIAlert[]>([]);
  const [messages, setMessages] = useState<GuestMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  useEffect(() => {
    // Load initial data
    fetchAlerts();
    fetchMessages();
    
    // Set up real-time updates
    const interval = setInterval(() => {
      fetchAlerts();
      fetchMessages();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    // Simulate AI-generated alerts
    const mockAlerts: AIAlert[] = [
      {
        id: '1',
        type: 'maintenance',
        severity: 'warning',
        title: 'AC Unit Room 12 Needs Attention',
        description: 'Efficiency dropped 15% in last 48 hours',
        aiSuggestion: 'Schedule preventive maintenance within 3 days to avoid breakdown',
        timestamp: '10 min ago'
      },
      {
        id: '2',
        type: 'inventory',
        severity: 'info',
        title: 'Coffee Pods Running Low',
        description: 'Current: 45 units. Predicted to run out in 3 days',
        aiSuggestion: 'Reorder 200 units now for best pricing',
        timestamp: '1 hour ago'
      },
      {
        id: '3',
        type: 'guest',
        severity: 'critical',
        title: 'Guest Complaint - Room 8',
        description: 'Negative sentiment detected in recent message',
        aiSuggestion: 'Immediate manager attention recommended',
        timestamp: 'Just now'
      }
    ];
    setAlerts(mockAlerts);
  };

  const fetchMessages = async () => {
    // Simulate guest messages with AI analysis
    const mockMessages: GuestMessage[] = [
      {
        id: '1',
        guestName: 'Maria Silva',
        room: '12',
        message: 'Is there a good sushi restaurant nearby?',
        sentiment: 'positive',
        intent: 'restaurant_recommendation',
        suggestedResponse: 'Yes! I recommend Sushi Yamato, just 5 minutes walk. They have excellent fresh fish and a special omakase menu. Would you like me to make a reservation?',
        timestamp: '2 min ago'
      },
      {
        id: '2',
        guestName: 'John Doe',
        room: '8',
        message: 'The AC is making a strange noise and not cooling properly',
        sentiment: 'negative',
        intent: 'maintenance_request',
        suggestedResponse: 'I apologize for the inconvenience. I\'m sending maintenance immediately to fix the AC. Meanwhile, would you like to move to another room?',
        timestamp: '5 min ago'
      }
    ];
    setMessages(mockMessages);
  };

  const sendAICommand = async () => {
    if (!chatInput.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/pousada/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: chatInput })
      });
      
      const data = await response.json();
      console.log('AI Response:', data);
      
      // Show response in UI
      setChatInput('');
    } catch (error) {
      console.error('AI command failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceInput = () => {
    setIsListening(!isListening);
    // In production, integrate with Web Speech API
    if (!isListening) {
      console.log('Starting voice recognition...');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-400';
      case 'negative': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const renderAIDashboard = () => (
    <div className="space-y-4">
      {/* AI Status Card */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">AI Assistant Active</h2>
              <p className="text-sm opacity-90">3 automations running</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">94%</div>
            <div className="text-xs opacity-90">Efficiency Score</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/20 rounded-lg p-2">
            <div className="font-bold">12</div>
            <div className="text-xs">Tasks Automated</div>
          </div>
          <div className="bg-white/20 rounded-lg p-2">
            <div className="font-bold">8</div>
            <div className="text-xs">Predictions Made</div>
          </div>
          <div className="bg-white/20 rounded-lg p-2">
            <div className="font-bold">€320</div>
            <div className="text-xs">Costs Saved</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
          <Package className="w-5 h-5 text-blue-400" />
          <div className="text-left">
            <div className="text-sm font-semibold">Inventory AI</div>
            <div className="text-xs text-gray-400">3 items low</div>
          </div>
        </button>
        <button className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
          <Calendar className="w-5 h-5 text-green-400" />
          <div className="text-left">
            <div className="text-sm font-semibold">Smart Schedule</div>
            <div className="text-xs text-gray-400">Optimized</div>
          </div>
        </button>
        <button className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          <div className="text-left">
            <div className="text-sm font-semibold">Predictions</div>
            <div className="text-xs text-gray-400">85% occupancy</div>
          </div>
        </button>
        <button className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
          <Users className="w-5 h-5 text-orange-400" />
          <div className="text-left">
            <div className="text-sm font-semibold">Guest AI</div>
            <div className="text-xs text-gray-400">2 pending</div>
          </div>
        </button>
      </div>

      {/* AI Command Input */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <span className="text-sm font-semibold">AI Command Center</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendAICommand()}
            placeholder="Ask AI anything..."
            className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
          />
          <button
            onClick={handleVoiceInput}
            className={`p-2 rounded-lg ${isListening ? 'bg-red-500' : 'bg-gray-700'}`}
          >
            <Mic className="w-5 h-5" />
          </button>
          <button
            onClick={sendAICommand}
            disabled={loading || !chatInput.trim()}
            className="bg-purple-600 p-2 rounded-lg disabled:opacity-50"
          >
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">AI Alerts</h2>
        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          {alerts.filter(a => a.severity === 'critical').length} Critical
        </span>
      </div>
      {alerts.map(alert => (
        <div key={alert.id} className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full mt-2 ${getSeverityColor(alert.severity)}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-white">{alert.title}</h3>
                <span className="text-xs text-gray-400">{alert.timestamp}</span>
              </div>
              <p className="text-sm text-gray-300 mb-2">{alert.description}</p>
              {alert.aiSuggestion && (
                <div className="bg-purple-900/30 border border-purple-600/30 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-semibold text-purple-400">AI Suggestion</span>
                  </div>
                  <p className="text-xs text-gray-300">{alert.aiSuggestion}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGuestChat = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Guest Messages</h2>
        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
          {messages.length} New
        </span>
      </div>
      {messages.map(msg => (
        <div key={msg.id} className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">{msg.guestName}</span>
                <span className="text-xs bg-gray-700 px-2 py-1 rounded">Room {msg.room}</span>
                <span className={`text-xs ${getSentimentColor(msg.sentiment)}`}>
                  ● {msg.sentiment}
                </span>
              </div>
              <span className="text-xs text-gray-400">{msg.timestamp}</span>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-3 mb-3">
            <p className="text-sm text-gray-300">{msg.message}</p>
          </div>

          <div className="bg-blue-900/30 border border-blue-600/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-semibold text-blue-400">
                  AI Analysis: {msg.intent.replace('_', ' ')}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-300 mb-2">{msg.suggestedResponse}</p>
            <div className="flex gap-2">
              <button className="flex-1 bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg">
                Send Response
              </button>
              <button className="bg-gray-700 text-white text-xs px-3 py-1.5 rounded-lg">
                Edit
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderInsights = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white mb-4">AI Insights</h2>
      
      {/* Predictive Analytics */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-4 text-white">
        <h3 className="font-semibold mb-3">Next 7 Days Predictions</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Occupancy Rate</span>
            <span className="font-bold">85%</span>
          </div>
          <div className="flex justify-between">
            <span>Revenue Forecast</span>
            <span className="font-bold">€12,450</span>
          </div>
          <div className="flex justify-between">
            <span>Staff Needed</span>
            <span className="font-bold">14 shifts</span>
          </div>
        </div>
      </div>

      {/* Optimization Suggestions */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="font-semibold text-white mb-3">AI Optimizations</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Dynamic Pricing Opportunity</p>
              <p className="text-xs text-gray-400">Increase weekend rates by 15% based on demand</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Staff Schedule Optimization</p>
              <p className="text-xs text-gray-400">Reduce Friday afternoon shift by 1 person</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Inventory Savings</p>
              <p className="text-xs text-gray-400">Bulk order amenities now to save €450</p>
            </div>
          </div>
        </div>
      </div>

      {/* Learning Progress */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="font-semibold text-white mb-3">AI Learning Progress</h3>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Guest Preferences</span>
              <span className="text-white">89%</span>
            </div>
            <div className="bg-gray-700 rounded-full h-2">
              <div className="bg-purple-500 h-2 rounded-full" style={{ width: '89%' }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Maintenance Patterns</span>
              <span className="text-white">76%</span>
            </div>
            <div className="bg-gray-700 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: '76%' }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Demand Forecasting</span>
              <span className="text-white">92%</span>
            </div>
            <div className="bg-gray-700 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: '92%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-purple-400" />
            <h1 className="text-lg font-bold text-white">Pousada AI</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-5 h-5 text-gray-400" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 pb-20">
        {activeTab === 'ai' && renderAIDashboard()}
        {activeTab === 'alerts' && renderAlerts()}
        {activeTab === 'chat' && renderGuestChat()}
        {activeTab === 'insights' && renderInsights()}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800">
        <div className="grid grid-cols-4 py-2">
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex flex-col items-center gap-1 py-2 ${
              activeTab === 'ai' ? 'text-purple-400' : 'text-gray-400'
            }`}
          >
            <Brain className="w-5 h-5" />
            <span className="text-xs">AI Hub</span>
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex flex-col items-center gap-1 py-2 ${
              activeTab === 'alerts' ? 'text-purple-400' : 'text-gray-400'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="text-xs">Alerts</span>
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex flex-col items-center gap-1 py-2 ${
              activeTab === 'chat' ? 'text-purple-400' : 'text-gray-400'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs">Chat</span>
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`flex flex-col items-center gap-1 py-2 ${
              activeTab === 'insights' ? 'text-purple-400' : 'text-gray-400'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <span className="text-xs">Insights</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PousadaMobileAI;